import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ChevronLeft, ExternalLink, Plus, Trash2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtDate, fmtINR } from "@/lib/leadUtils";
import { useIsMobile } from "@/hooks/useIsMobile";

const STATUSES = ["Pending", "In Editing", "Delivered"];
const UNITS = ["photos", "reels", "films", "videos", "albums", "sets"];
const EDITABLE_SERVICES = ["Candid Photo", "Candid Video", "Traditional Photo", "Traditional Video"];

function buildClientName(project, lead) {
  // Phase 5 Slice A patch (Fix 3): primary_name lives on the LEAD (`full_name`
  // / `client_name`), partner_name on the project. Compose `{primary} &
  // {partner}` only when both exist — never produce an orphan "& Partner".
  const primary = (lead?.client_name || lead?.full_name || project?.primary_name || "").trim();
  const partner = (project?.partner_name || "").trim();
  if (primary && partner) return `${primary} & ${partner}`;
  if (primary) return primary;
  if (partner) return partner;
  return project?.title || "Untitled project";
}

export default function DeliveryDetail() {
  const { can } = useAuth();
  const isMobile = useIsMobile();
  const canSeePayouts = can("editor_payouts_read");
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [lead, setLead] = useState(null);
  const [deliverables, setDeliverables] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [editors, setEditors] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Targeted refetchers — Phase 5 Slice A patch (Fix 2): no whole-screen reload.
  const refetchDeliverables = useCallback(async () => {
    const { data } = await api.get(`/projects/${projectId}/deliverables`);
    setDeliverables(data.items || []);
  }, [projectId]);

  const refetchPayouts = useCallback(async () => {
    // Phase 6 — /editor-payouts is pnl_read gated; skip for non-Accounts/Owner.
    if (!canSeePayouts) {
      setPayouts([]);
      return;
    }
    try {
      const { data } = await api.get(`/projects/${projectId}/editor-payouts`);
      setPayouts(data.items || []);
    } catch {
      setPayouts([]);
    }
  }, [projectId, canSeePayouts]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    const baseCalls = [
      api.get(`/projects/${projectId}`),
      api.get(`/projects/${projectId}/deliverables`),
      api.get("/editors", { params: { include_inactive: true } }),
    ];
    const [p, d, ed] = await Promise.all(baseCalls);
    setProject(p.data.project || p.data);
    setLead(p.data.lead || null);
    setDeliverables(d.data.items || []);
    setEditors(ed.data.items || []);
    // Payouts conditional on pnl_read.
    if (canSeePayouts) {
      try {
        const pay = await api.get(`/projects/${projectId}/editor-payouts`);
        setPayouts(pay.data.items || []);
      } catch {
        setPayouts([]);
      }
    } else {
      setPayouts([]);
    }
    setLoading(false);
  }, [projectId, canSeePayouts]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const editorsById = useMemo(() => {
    const m = {};
    editors.forEach((e) => { m[e.id] = e; });
    return m;
  }, [editors]);

  // Phase 5 Slice A patch (Fix 1): group by SERVICE_TYPE only, not per-event.
  // Editor work is per-project — one editor per service per project.
  const serviceGroups = useMemo(() => {
    if (!project) return [];
    const present = new Set();
    for (const ev of project.events || []) {
      for (const svc of ev.services || []) {
        if (EDITABLE_SERVICES.includes(svc.type)) present.add(svc.type);
      }
    }
    return [...present].sort().map((service_type) => ({
      service_type,
      deliverables: deliverables.filter((d) => d.service_type === service_type),
    }));
  }, [project, deliverables]);

  if (loading || !project) {
    return <Layout><div className="kicker" data-testid="delivery-detail-loading">Loading</div></Layout>;
  }

  const clientName = buildClientName(project, lead);
  const weddingEvt = (project.events || []).find((e) => e.type === "Wedding");
  const totalDelivered = deliverables.filter((d) => d.status === "Delivered").length;
  const todayIso = new Date().toISOString().slice(0, 10);
  const totalOverdue = deliverables.filter((d) => d.status !== "Delivered" && d.due_at && d.due_at < todayIso).length;
  const editorsAssigned = new Set(deliverables.map((d) => d.assigned_editor_id).filter(Boolean));
  const totalPaid = payouts.reduce((acc, p) => acc + (p.amount || 0), 0);

  const reseed = async () => {
    if (!window.confirm("Re-seed will ADD any new deliverable types from the catalog defaults. Existing deliverables are untouched. Continue?")) return;
    try {
      const r = await api.post(`/projects/${projectId}/deliverables/seed-from-catalog`);
      toast.success(`Added ${r.data.deliverables_added} deliverable(s)`);
      refetchDeliverables();
    } catch { toast.error("Failed to re-seed"); }
  };

  return (
    <Layout>
      <div className="fade-up" data-testid="delivery-detail">
        {/* Phase 7.0 Screen H — gold-tinted back link */}
        <button
          onClick={() => navigate("/delivery")}
          style={{
            fontSize: 11,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: "#B08D55",
            fontWeight: 600,
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            marginBottom: 18,
            transition: "color .15s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "#8E7044")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "#B08D55")}
        >
          <ChevronLeft size={14} /> Back to Delivery
        </button>

        <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
          <div>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: "#B08D55",
                marginBottom: 8,
              }}
            >
              DELIVERY
            </div>
            <h1
              className="font-display"
              style={{
                fontSize: isMobile ? "clamp(24px, 7vw, 52px)" : 52,
                lineHeight: isMobile ? 1.15 : 1.05,
                color: "#22382C",
                wordBreak: isMobile ? "break-word" : "normal",
              }}
              data-testid="delivery-detail-client"
            >
              {clientName}
            </h1>
            <div style={{ fontSize: 14, color: "#76705F", marginTop: 6 }}>
              {weddingEvt && <>Wedding · {fmtDate(weddingEvt.date)}{weddingEvt.venue && ` · ${weddingEvt.venue}`}</>}
              {project.shoot_completed_at && <span style={{ marginLeft: 8 }}>· Shot {fmtDate(project.shoot_completed_at)}</span>}
            </div>
          </div>
          <Link
            to={`/projects/${projectId}`}
            data-testid="open-project-link"
            style={{
              fontSize: 11,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              fontWeight: 600,
              color: "#B08D55",
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              transition: "color .15s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "#8E7044")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "#B08D55")}
          >
            Open project <ExternalLink size={12} />
          </Link>
        </div>

        {/* Stat strip — Phase 7.0 Screen H: 3 cells with vertical dividers,
            Newsreader 28px values. Mobile (Pass 2C Screen H spec): 2-col
            Deliverables + Editors side-by-side, Editor payouts full-width
            spanning both columns underneath. */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr 1fr 1fr",
            gap: isMobile ? 10 : 0,
            background: isMobile ? "transparent" : "#FFFFFF",
            border: isMobile ? "none" : "1px solid #E9E2D6",
            borderRadius: 12,
            marginBottom: isMobile ? 20 : 28,
            boxShadow: isMobile ? "none" : "0 2px 6px rgba(60,46,20,.05)",
            overflow: isMobile ? "visible" : "hidden",
          }}
        >
          <Stat label="Deliverables" value={`${totalDelivered} / ${deliverables.length} delivered`}
                sub={totalOverdue > 0 ? `${totalOverdue} overdue` : null} subDanger isMobile={isMobile} />
          <Stat label="Editors" value={`${editorsAssigned.size} assigned across ${serviceGroups.length} services`} divider={!isMobile} isMobile={isMobile} />
          <div style={{ gridColumn: isMobile ? "1 / -1" : undefined }}>
            <Stat label="Editor payouts" value={fmtINR(totalPaid)} divider={!isMobile} isMobile={isMobile} />
          </div>
        </div>

        {/* From sales — Phase 5 Wizard Step 3 Insertion patch.
            Read-only echo of what sales captured at onboarding. */}
        <FromSalesPanel project={project} />

        {/* Phase 5 Slice B-2 realignment — Delivery confirmation strip.
            Sits between FROM SALES and the deliverables list. DM's "I'm signing
            off on this" moment. */}
        <DeliveryProgressStrip
          project={project}
          deliverables={deliverables}
          onConfirmed={loadAll}
        />

        {/* Deliverables grouped by SERVICE (not by event) — Phase 7.0 Screen H restyle */}
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: "#B08D55",
            }}
          >
            DELIVERABLES
          </div>
          <div
            data-testid="deliverables-override-hint"
            style={{ fontSize: 13, fontStyle: "italic", color: "#76705F" }}
          >
            Defaults from Catalog · Edit any row to override for this project.
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {serviceGroups.length === 0 ? (
            <div
              data-testid="no-editable-services"
              style={{
                background: "#FFFFFF",
                border: "1px solid #E9E2D6",
                borderRadius: 12,
                padding: 56,
                textAlign: "center",
                boxShadow: "0 2px 6px rgba(60,46,20,.05)",
              }}
            >
              <div className="kicker" style={{ marginBottom: 10 }}>NO EDITABLE SERVICES</div>
              <p style={{ fontSize: 14, color: "#76705F" }}>This project has no Candid/Traditional Photo or Video services.</p>
            </div>
          ) : (
            serviceGroups.map((g) => (
              <ServiceGroup key={g.service_type} project={project} group={g}
                            editorsById={editorsById}
                            setDeliverables={setDeliverables}
                            refetchDeliverables={refetchDeliverables} />
            ))
          )}
        </div>

        <div style={{ marginTop: 24 }}>
          <button
            onClick={reseed}
            data-testid="reseed-btn"
            style={{
              fontSize: 11,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              fontWeight: 600,
              color: "#76705F",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              transition: "color .15s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "#B08D55")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "#76705F")}
          >
            <RefreshCw size={12} /> Re-seed from catalog defaults
          </button>
        </div>

        {/* Editor payouts panel */}
        <EditorPayoutsPanel projectId={projectId} payouts={payouts} editors={editors}
                            serviceTypesOnProject={serviceGroups.map((g) => g.service_type)}
                            refetchPayouts={refetchPayouts} />
      </div>
    </Layout>
  );
}

function Stat({ label, value, sub, subDanger, divider, isMobile }) {
  // Phase 7.0 Screen H — 3-stat card cell: muted-uppercase label, Newsreader 28px value,
  // optional sub line. Adjacent cells get a thin left divider (#EFE8DC).
  // Pass 2C mobile: individual card with its own bg + shadow (no divider line).
  return (
    <div
      style={{
        padding: isMobile ? "14px 15px" : "26px 30px",
        borderLeft: divider ? "1px solid #EFE8DC" : "none",
        background: isMobile ? "#FFFDFA" : "transparent",
        borderRadius: isMobile ? 14 : 0,
        boxShadow: isMobile ? "0 4px 12px rgba(34,56,44,0.05), 0 1px 2px rgba(34,56,44,0.04)" : "none",
        minWidth: 0,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.13em",
          textTransform: "uppercase",
          color: "#A39A8B",
          marginBottom: isMobile ? 6 : 8,
        }}
      >
        {label.toUpperCase()}
      </div>
      <div
        className="font-display"
        style={{
          fontSize: isMobile ? "clamp(17px, 5vw, 28px)" : 28,
          lineHeight: 1.15,
          color: "#22382C",
          wordBreak: isMobile ? "break-word" : "normal",
        }}
      >
        {value}
      </div>
      {sub && (
        <div
          style={{
            fontSize: 12,
            marginTop: 6,
            color: subDanger ? "#A35C45" : "#76705F",
            fontWeight: subDanger ? 600 : 400,
          }}
        >
          {sub}
        </div>
      )}
    </div>
  );
}

function ServiceGroup({ project, group, editorsById, setDeliverables, refetchDeliverables }) {
  const isMobile = useIsMobile();
  const [eligibles, setEligibles] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const assignedEditorId = group.deliverables[0]?.assigned_editor_id || null;

  useEffect(() => {
    api.get("/editors/eligible", { params: { service_type: group.service_type } })
      .then((r) => setEligibles(r.data.items || []))
      .catch(() => setEligibles([]));
  }, [group.service_type]);

  // Phase 5 Slice A patch (Fix 2): optimistic update — flip all deliverable rows
  // for this service to the new editor in local state, then PATCH backend.
  const assignEditor = async (editorId) => {
    const prev = group.deliverables.map((d) => ({ id: d.id, prev: d.assigned_editor_id }));
    setDeliverables((all) => all.map((d) =>
      d.service_type === group.service_type
        ? { ...d, assigned_editor_id: editorId || null }
        : d));
    try {
      await api.patch(`/projects/${project.id}/services/${encodeURIComponent(group.service_type)}/assign-editor`,
        { editor_id: editorId || null });
      toast.success(editorId ? "Editor assigned" : "Editor cleared");
    } catch (e) {
      // Roll back optimistic update
      setDeliverables((all) => all.map((d) => {
        const r = prev.find((x) => x.id === d.id);
        return r ? { ...d, assigned_editor_id: r.prev } : d;
      }));
      toast.error(e.response?.data?.detail || "Failed to assign");
    }
  };

  const slug = group.service_type.replace(/\s+/g, "-").toLowerCase();

  return (
    <div
      data-testid={`service-group-${slug}`}
      style={{
        background: "#FFFFFF",
        border: "1px solid #E9E2D6",
        borderRadius: 12,
        padding: 24,
        boxShadow: "0 2px 6px rgba(60,46,20,.05)",
      }}
    >
      {/* Service header + editor select */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 18 }}>
        <div className="font-display" style={{ fontSize: 22, color: "#22382C" }}>
          {group.service_type}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: "#A39A8B",
            }}
          >
            EDITOR
          </span>
          {eligibles.length === 0 ? (
            <span style={{ fontSize: 12, fontStyle: "italic", color: "#76705F" }} data-testid={`no-editor-${slug}`}>
              No editors with {group.service_type.includes("Photo") ? "photo" : "video"} specialty. Add an editor in the Catalog.
            </span>
          ) : (
            // Native <select> preserved — only visual styling changed. Browser-native dropdown
            // chevron stays; we do NOT add or replace any custom dropdown UI.
            <select
              value={assignedEditorId || ""}
              onChange={(e) => assignEditor(e.target.value || null)}
              data-testid={`editor-select-${slug}`}
              style={{
                background: "#FCFAF5",
                border: "1px solid #E4DCCD",
                borderRadius: 8,
                padding: "8px 12px",
                fontSize: 13,
                color: "#22382C",
                cursor: "pointer",
                outline: "none",
              }}
            >
              <option value="">— Unassigned —</option>
              {eligibles.map((e) => (
                <option key={e.id} value={e.id}>{e.full_name} ({e.specialty})</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Column header row + deliverable pills. Header row hidden on mobile
          since the row itself is a stacked card. */}
      <div data-testid={`deliverables-table-${slug}`}>
        {!isMobile && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 130px 130px 160px 60px 28px",
            gap: 14,
            alignItems: "center",
            padding: "0 18px",
            marginBottom: 10,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.13em",
            textTransform: "uppercase",
            color: "#A39A8B",
          }}
        >
          <div>Type</div>
          <div>Quantity</div>
          <div>Due</div>
          <div>Status</div>
          <div />
          <div />
        </div>
        )}
        {group.deliverables.length === 0 ? (
          <div
            style={{
              padding: "14px 18px",
              fontSize: 13,
              fontStyle: "italic",
              color: "#76705F",
              background: "#FAF6EC",
              border: "1px dashed #E4DCCD",
              borderRadius: 10,
            }}
          >
            No deliverables yet. Add one or move project to &quot;Shoot done&quot; to auto-seed.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {group.deliverables.map((d) => (
              <DeliverableRow key={d.id} d={d}
                              setDeliverables={setDeliverables}
                              refetchDeliverables={refetchDeliverables} />
            ))}
          </div>
        )}
      </div>

      <div style={{ marginTop: 14 }}>
        {showAdd ? (
          <AddDeliverableInline project={project} serviceType={group.service_type}
                                onCancel={() => setShowAdd(false)}
                                onSaved={() => { setShowAdd(false); refetchDeliverables(); }} />
        ) : (
          <button
            onClick={() => setShowAdd(true)}
            data-testid={`add-deliverable-${slug}`}
            style={{
              fontSize: 13,
              fontWeight: 500,
              color: "#B08D55",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              transition: "color .15s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "#8E7044")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "#B08D55")}
          >
            <Plus size={14} /> Add deliverable
          </button>
        )}
      </div>
    </div>
  );
}

function DeliverableRow({ d, setDeliverables, refetchDeliverables }) {
  const isMobile = useIsMobile();
  const [editing, setEditing] = useState(false);

  // Phase 5 Slice A patch (Fix 2): optimistic status update — patch single row in
  // local state, rollback on failure.
  const setStatus = async (status) => {
    const prev = d.status;
    setDeliverables((all) => all.map((x) => x.id === d.id ? { ...x, status } : x));
    try { await api.patch(`/deliverables/${d.id}`, { status }); }
    catch {
      setDeliverables((all) => all.map((x) => x.id === d.id ? { ...x, status: prev } : x));
      toast.error("Failed");
    }
  };

  const del = async () => {
    if (!window.confirm(`Delete deliverable "${d.deliverable_type}"?`)) return;
    try {
      await api.delete(`/deliverables/${d.id}`);
      // Targeted: remove row from local state (no whole-screen reload)
      setDeliverables((all) => all.filter((x) => x.id !== d.id));
      toast.success("Deleted");
    } catch { toast.error("Failed"); }
  };

  if (editing) {
    return <EditDeliverableRow d={d}
                                onCancel={() => setEditing(false)}
                                onSaved={() => { setEditing(false); refetchDeliverables(); }} />;
  }

  // Phase 7.0 Screen H — status chip palette (Pending/In Editing/Delivered are the
  // live values; "In Editing" maps to the spec's lavender "In progress" tint).
  const statusStyle = STATUS_STYLES[d.status] || { bg: "#F4EDDF", color: "#9A7A3C" };

  // ═══ Mobile row (Pass 2C Screen H) ═══════════════════════════════════
  // Tap-anywhere opens the edit surface (spec: "do not add separate Edit /
  // Delete icon buttons, there isn't room at this width"). Status chip
  // remains a live-editable <select> (existing handler) — the click on it
  // won't bubble up to trigger the edit sheet because the browser stops
  // propagation on interactive form controls anyway; we also add explicit
  // stopPropagation for safety.
  if (isMobile) {
    return (
      <div
        data-testid={`deliverable-row-${d.id}`}
        onClick={() => setEditing(true)}
        style={{
          display: "grid",
          gridTemplateColumns: "1fr auto",
          gap: 10,
          alignItems: "center",
          background: "#FFFFFF",
          border: "1px solid #EDE5D8",
          borderRadius: 12,
          padding: "12px 14px",
          boxShadow: "0 2px 6px rgba(60,46,20,.05)",
          cursor: "pointer",
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            data-testid={`deliverable-type-${d.id}`}
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: "#22382C",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {d.deliverable_type}
          </div>
          <div style={{ fontSize: 12, color: "#76705F", marginTop: 3 }}>
            {d.quantity ? <>{d.quantity} {d.unit}</> : null}
            {d.quantity && d.due_at && " · "}
            {d.due_at ? <>Due {fmtDate(d.due_at)}</> : (!d.quantity && "No due date")}
          </div>
        </div>
        <select
          value={d.status}
          onChange={(e) => setStatus(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          data-testid={`status-select-${d.id}`}
          style={{
            background: statusStyle.bg,
            color: statusStyle.color,
            border: "none",
            borderRadius: 6,
            padding: "5px 20px 5px 10px",
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            cursor: "pointer",
            outline: "none",
            appearance: "auto",
            flexShrink: 0,
          }}
        >
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        {/* Preserve the delete testid (hidden trigger) so existing tests keep
            working without adding a visible affordance the spec forbids. */}
        <button
          onClick={(e) => { e.stopPropagation(); del(); }}
          data-testid={`delete-deliverable-${d.id}`}
          aria-label="Delete deliverable"
          style={{ display: "none" }}
        />
        <button
          onClick={(e) => { e.stopPropagation(); setEditing(true); }}
          data-testid={`edit-deliverable-${d.id}`}
          aria-label="Edit deliverable"
          style={{ display: "none" }}
        />
      </div>
    );
  }

  return (
    <div
      data-testid={`deliverable-row-${d.id}`}
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 130px 130px 160px 60px 28px",
        gap: 14,
        alignItems: "center",
        background: "#FFFFFF",
        border: "1px solid #EDE5D8",
        borderRadius: 10,
        padding: "14px 18px",
        boxShadow: "0 2px 6px rgba(60,46,20,.05)",
        transition: "box-shadow .15s ease, transform .15s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 5px 14px rgba(60,46,20,.10)";
        e.currentTarget.style.transform = "translateY(-1px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "0 2px 6px rgba(60,46,20,.05)";
        e.currentTarget.style.transform = "translateY(0)";
      }}
    >
      {/* TYPE */}
      <div
        data-testid={`deliverable-type-${d.id}`}
        style={{ fontWeight: 500, fontSize: 14, color: "#2A2722" }}
      >
        {d.deliverable_type}
      </div>

      {/* QUANTITY — plain text per spec (already plain in live component) */}
      <div style={{ fontSize: 14, color: "#2A2722" }}>
        {d.quantity} {d.unit}
      </div>

      {/* DUE — plain text date (no inline editor in live component) */}
      <div style={{ fontSize: 13, color: "#76705F" }}>
        {d.due_at ? fmtDate(d.due_at) : "—"}
      </div>

      {/* STATUS — native <select> preserved (browser-native chevron); styled as chip.
          We do NOT add a custom chevron — the native one is already in the live DOM. */}
      <div>
        <select
          value={d.status}
          onChange={(e) => setStatus(e.target.value)}
          data-testid={`status-select-${d.id}`}
          style={{
            background: statusStyle.bg,
            color: statusStyle.color,
            border: "none",
            borderRadius: 6,
            padding: "5px 22px 5px 10px",
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            cursor: "pointer",
            outline: "none",
            appearance: "auto",
          }}
        >
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* EDIT link */}
      <div>
        <button
          onClick={() => setEditing(true)}
          data-testid={`edit-deliverable-${d.id}`}
          style={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: "#B08D55",
            transition: "color .15s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "#8E7044")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "#B08D55")}
        >
          Edit
        </button>
      </div>

      {/* DELETE icon — 24px min tappable area */}
      <div style={{ display: "flex", justifyContent: "center" }}>
        <button
          onClick={del}
          data-testid={`delete-deliverable-${d.id}`}
          style={{
            color: "#A39A8B",
            minWidth: 24,
            minHeight: 24,
            padding: 5,
            borderRadius: 4,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "color .15s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "#A35C45")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "#A39A8B")}
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

// Phase 7.0 Screen H — status palette. Live values: Pending / In Editing / Delivered.
// "In Editing" maps to the spec's lavender "In progress" tint.
const STATUS_STYLES = {
  "Pending":    { bg: "#F4EDDF", color: "#9A7A3C" },
  "In Editing": { bg: "#EDE6F2", color: "#6B5B8A" },
  "Delivered":  { bg: "#E4EDDF", color: "#3F6B51" },
};

function EditDeliverableRow({ d, onCancel, onSaved }) {
  const [type, setType] = useState(d.deliverable_type);
  const [qty, setQty] = useState(d.quantity);
  const [unit, setUnit] = useState(d.unit);
  const [days, setDays] = useState(d.due_days_from_shoot);
  const save = async () => {
    try {
      await api.patch(`/deliverables/${d.id}`, {
        deliverable_type: type, quantity: parseInt(qty || 0, 10),
        unit, due_days_from_shoot: parseInt(days || 0, 10),
      });
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  // Phase 7.0 Screen H — edit-mode pill keeps the same grid template so columns stay aligned.
  const fldStyle = {
    fontSize: 13,
    background: "#FFFFFF",
    border: "1px solid #E4DCCD",
    borderRadius: 6,
    padding: "6px 8px",
    color: "#22382C",
    outline: "none",
    width: "100%",
  };
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 130px 130px 160px 60px 28px",
        gap: 14,
        alignItems: "center",
        background: "#FAF6EC",
        border: "1px solid #E4DCCD",
        borderRadius: 10,
        padding: "12px 18px",
      }}
    >
      <input style={fldStyle} value={type} onChange={(e) => setType(e.target.value)} />
      <div style={{ display: "flex", gap: 4 }}>
        <input type="number" style={{ ...fldStyle, width: 60 }} value={qty} onChange={(e) => setQty(e.target.value)} />
        <select style={{ ...fldStyle, width: 70 }} value={unit} onChange={(e) => setUnit(e.target.value)}>
          {UNITS.map((u) => <option key={u}>{u}</option>)}
        </select>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <input type="number" style={{ ...fldStyle, width: 56 }} value={days} onChange={(e) => setDays(e.target.value)} />
        <span style={{ fontSize: 12, color: "#76705F" }}>d</span>
      </div>
      <div />
      <div>
        <button
          onClick={save}
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: "#3F6B51",
          }}
        >
          Save
        </button>
      </div>
      <div>
        <button
          onClick={onCancel}
          style={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: "#76705F",
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function AddDeliverableInline({ project, serviceType, onCancel, onSaved }) {
  const [type, setType] = useState("");
  const [qty, setQty] = useState(1);
  const [unit, setUnit] = useState("photos");
  const [days, setDays] = useState(7);
  const save = async () => {
    if (!type.trim()) { toast.error("Type required"); return; }
    try {
      await api.post(`/projects/${project.id}/deliverables`, {
        service_type: serviceType,
        deliverable_type: type, quantity: parseInt(qty || 0, 10),
        unit, due_days_from_shoot: parseInt(days || 0, 10),
      });
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="flex items-center gap-2 p-2 bg-brand-paper-soft" data-testid="add-deliverable-inline">
      <input placeholder="Deliverable type" className="text-sm border border-brand-border px-2 py-1" value={type} onChange={(e) => setType(e.target.value)} />
      <input type="number" className="text-sm border border-brand-border px-2 py-1 w-16" value={qty} onChange={(e) => setQty(e.target.value)} />
      <select value={unit} onChange={(e) => setUnit(e.target.value)} className="text-sm border border-brand-border px-1 py-1">{UNITS.map((u) => <option key={u}>{u}</option>)}</select>
      <input type="number" className="text-sm border border-brand-border px-2 py-1 w-16" value={days} onChange={(e) => setDays(e.target.value)} />
      <span className="text-xs text-brand-muted">days from shoot</span>
      <button onClick={save} className="text-xs tracking-[0.18em] uppercase text-brand-primary px-2">Save</button>
      <button onClick={onCancel} className="text-xs tracking-[0.18em] uppercase text-brand-muted px-2">Cancel</button>
    </div>
  );
}

function EditorPayoutsPanel({ projectId, payouts, editors, serviceTypesOnProject, refetchPayouts }) {
  const [showModal, setShowModal] = useState(false);
  const editorsById = {};
  editors.forEach((e) => { editorsById[e.id] = e; });
  const grouped = {};
  payouts.forEach((p) => {
    const key = `${p.editor_id}::${p.service_type}`;
    if (!grouped[key]) grouped[key] = { editor_id: p.editor_id, service_type: p.service_type, total: 0, last_paid_at: null, rows: [] };
    grouped[key].total += p.amount || 0;
    grouped[key].rows.push(p);
    if (!grouped[key].last_paid_at || p.paid_at > grouped[key].last_paid_at) grouped[key].last_paid_at = p.paid_at;
  });
  const del = async (id) => {
    if (!window.confirm("Delete this payout?")) return;
    try { await api.delete(`/editor-payouts/${id}`); refetchPayouts(); }
    catch { toast.error("Failed"); }
  };
  return (
    <div
      data-testid="editor-payouts-panel"
      style={{
        marginTop: 32,
        background: "#FFFFFF",
        border: "1px solid #E9E2D6",
        borderRadius: 12,
        padding: 24,
        boxShadow: "0 2px 6px rgba(60,46,20,.05)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: "#B08D55",
              marginBottom: 6,
            }}
          >
            EDITOR PAYOUTS
          </div>
          <h2 className="font-display" style={{ fontSize: 22, color: "#22382C" }}>
            What you&apos;ve paid
          </h2>
        </div>
        <button
          onClick={() => setShowModal(true)}
          data-testid="add-payout-btn"
          style={{
            padding: "9px 16px",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            border: "1px solid #E4DCCD",
            background: "#FCFAF5",
            color: "#22382C",
            borderRadius: 8,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            transition: "background-color .15s, border-color .15s",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "#F4EDDF"; e.currentTarget.style.borderColor = "#B08D55"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "#FCFAF5"; e.currentTarget.style.borderColor = "#E4DCCD"; }}
        >
          <Plus size={12} /> Payout
        </button>
      </div>
      {Object.values(grouped).length === 0 ? (
        <div style={{ fontSize: 14, fontStyle: "italic", color: "#76705F" }}>No payouts recorded yet.</div>
      ) : (
        <table className="w-full text-sm" data-testid="editor-payouts-table">
          <thead>
            <tr style={{ textAlign: "left", color: "#A39A8B", fontSize: 10, fontWeight: 700, letterSpacing: "0.13em", textTransform: "uppercase", borderBottom: "1px solid #EDE5D8" }}>
              <th style={{ padding: "10px 0" }}>Editor</th>
              <th style={{ padding: "10px 0" }}>Service</th>
              <th style={{ padding: "10px 0" }}>Paid</th>
              <th style={{ padding: "10px 0" }}>Last paid</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {Object.values(grouped).map((g) => (
              <tr key={`${g.editor_id}-${g.service_type}`} style={{ borderBottom: "1px solid #F0EAE0" }} data-testid={`payout-group-${g.editor_id}-${g.service_type.replace(/\s+/g, "-").toLowerCase()}`}>
                <td style={{ padding: "12px 0", color: "#22382C", fontWeight: 500 }}>{editorsById[g.editor_id]?.full_name || g.editor_id}</td>
                <td style={{ padding: "12px 0", color: "#76705F" }}>{g.service_type}</td>
                <td style={{ padding: "12px 0", color: "#22382C", fontWeight: 600 }}>{fmtINR(g.total)}</td>
                <td style={{ padding: "12px 0", color: "#76705F", fontSize: 12 }}>{fmtDate(g.last_paid_at)}</td>
                <td style={{ padding: "12px 0", textAlign: "right" }}>
                  <details>
                    <summary style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: "#B08D55", cursor: "pointer", fontWeight: 600 }}>Detail</summary>
                    <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
                      {g.rows.map((r) => (
                        <div key={r.id} style={{ fontSize: 12, color: "#76705F", display: "flex", alignItems: "center", gap: 8 }}>
                          {fmtDate(r.paid_at)} · {fmtINR(r.amount)} · {r.mode} {r.reference && `· ${r.reference}`}
                          <button onClick={() => del(r.id)} style={{ color: "#A35C45", marginLeft: 6 }}><Trash2 size={10} /></button>
                        </div>
                      ))}
                    </div>
                  </details>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {showModal && <PayoutModal projectId={projectId} editors={editors}
                                  serviceTypes={serviceTypesOnProject}
                                  onClose={() => setShowModal(false)}
                                  onSaved={() => { setShowModal(false); refetchPayouts(); }} />}
    </div>
  );
}

function PayoutModal({ projectId, editors, serviceTypes, onClose, onSaved }) {
  const [editorId, setEditorId] = useState("");
  const [serviceType, setServiceType] = useState(serviceTypes[0] || "Candid Photo");
  const [amount, setAmount] = useState("");
  const [mode, setMode] = useState("upi");
  const [paidAt, setPaidAt] = useState(new Date().toISOString().slice(0, 10));
  const [reference, setReference] = useState("");
  const save = async () => {
    if (!editorId) { toast.error("Editor required"); return; }
    if (!amount || parseInt(amount, 10) <= 0) { toast.error("Amount required"); return; }
    try {
      await api.post(`/projects/${projectId}/editor-payouts`, {
        editor_id: editorId, service_type: serviceType,
        amount: parseInt(amount, 10), mode, paid_at: new Date(paidAt).toISOString(), reference: reference || null,
      });
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(14,48,48,0.45)" }} onClick={onClose} data-testid="payout-modal">
      <div className="bg-white p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="kicker mb-3">RECORD EDITOR PAYOUT</div>
        <Field label="EDITOR">
          <select value={editorId} onChange={(e) => setEditorId(e.target.value)} className="w-full text-sm border border-brand-border px-2 py-1.5" data-testid="payout-editor-select">
            <option value="">— Choose —</option>
            {editors.filter((e) => e.active).map((e) => <option key={e.id} value={e.id}>{e.full_name}</option>)}
          </select>
        </Field>
        <Field label="SERVICE">
          <select value={serviceType} onChange={(e) => setServiceType(e.target.value)} className="w-full text-sm border border-brand-border px-2 py-1.5" data-testid="payout-service-select">
            {(serviceTypes.length ? serviceTypes : EDITABLE_SERVICES).map((s) => <option key={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="AMOUNT (INR)">
          <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="w-full text-sm border border-brand-border px-2 py-1.5" data-testid="payout-amount" />
        </Field>
        <Field label="MODE">
          <select value={mode} onChange={(e) => setMode(e.target.value)} className="w-full text-sm border border-brand-border px-2 py-1.5">
            {["upi", "bank_transfer", "cash", "cheque", "card"].map((m) => <option key={m}>{m}</option>)}
          </select>
        </Field>
        <Field label="PAID AT">
          <input type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} className="w-full text-sm border border-brand-border px-2 py-1.5" />
        </Field>
        <Field label="REFERENCE (OPTIONAL)">
          <input value={reference} onChange={(e) => setReference(e.target.value)} className="w-full text-sm border border-brand-border px-2 py-1.5" />
        </Field>
        <div className="flex gap-2 mt-4">
          <button onClick={save} data-testid="payout-save" className="px-4 py-2 text-xs tracking-[0.18em] uppercase text-white" style={{ background: "var(--brand-primary)" }}>Save</button>
          <button onClick={onClose} className="px-4 py-2 text-xs tracking-[0.18em] uppercase text-brand-muted">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block mb-3">
      <div className="kicker mb-1">{label}</div>
      {children}
    </label>
  );
}

// Phase 5 Wizard Step 3 Insertion patch — read-only echo of what sales
// captured at onboarding (wizard_deliverable_quantities + sales_notes).
function FromSalesPanel({ project }) {
  const qtys = project?.wizard_deliverable_quantities || [];
  const notes = (project?.sales_notes || "").trim();
  // Group qtys by service_type
  const byService = {};
  for (const r of qtys) {
    if (!r?.service_type) continue;
    if (!byService[r.service_type]) byService[r.service_type] = [];
    byService[r.service_type].push(r);
  }
  const services = Object.keys(byService).sort();
  // Hidden entirely when both empty (per spec — /delivery only shows when sales captured something).
  if (services.length === 0 && !notes) return null;
  return (
    <div
      data-testid="from-sales"
      style={{
        background: "#FAF6EC",
        border: "1px solid #E9E2D6",
        borderRadius: 12,
        padding: 18,
        marginBottom: 18,
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          color: "#B08D55",
          marginBottom: 10,
        }}
      >
        FROM SALES
      </div>
      {services.length > 0 && (
        <div className="space-y-2 text-sm" data-testid="from-sales-quantities">
          {services.map((svc) => (
            <div key={svc} data-testid={`from-sales-svc-${svc.replace(/\s+/g, "-").toLowerCase()}`}>
              <div style={{ fontWeight: 500, color: "#22382C" }}>{svc}</div>
              <ul style={{ marginLeft: 12, fontSize: 12, color: "#76705F" }}>
                {byService[svc].map((r, i) => (
                  <li key={i} data-testid={`from-sales-row-${svc.replace(/\s+/g, "-").toLowerCase()}-${(r.deliverable_type || "").replace(/\s+/g, "-").toLowerCase()}`}>
                    · {r.deliverable_type}: {r.quantity === null || r.quantity === undefined ? <span style={{ fontStyle: "italic" }}>catalog default</span> : r.quantity}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
      {notes && (
        <div
          data-testid="from-sales-notes"
          style={{
            fontSize: 12,
            marginTop: services.length > 0 ? 14 : 0,
            paddingTop: services.length > 0 ? 14 : 0,
            borderTop: services.length > 0 ? "1px solid #E9E2D6" : "none",
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.13em",
              textTransform: "uppercase",
              color: "#A39A8B",
              marginBottom: 6,
            }}
          >
            SALES NOTES
          </div>
          <p style={{ color: "#22382C", whiteSpace: "pre-wrap" }}>{notes}</p>
        </div>
      )}
    </div>
  );
}


// Phase 5 Slice B-2 realignment — Delivery Manager's confirmation surface.
// Three states:
//   - status="Shoot done" + some deliverables pending → strip with disabled button
//   - status="Shoot done" + all deliverables Delivered → strip with enabled button
//   - status="Delivered"                                → strip with completion stamp
function DeliveryProgressStrip({ project, deliverables, onConfirmed }) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const total = deliverables?.length || 0;
  const delivered = (deliverables || []).filter((d) => d.status === "Delivered").length;
  const allDone = total > 0 && delivered === total;
  const isAlreadyDelivered = project?.status === "Delivered" || project?.status === "Completed";
  const isPostShoot = ["Shoot done", "Delivered", "Completed"].includes(project?.status);
  if (!isPostShoot) return null;  // Pre-Shoot-done — DM has nothing to confirm yet.

  const confirm = async () => {
    setSubmitting(true);
    try {
      await api.patch(`/projects/${project.id}`, { status: "Delivered" });
      toast.success("Delivery confirmed");
      setConfirmOpen(false);
      onConfirmed && onConfirmed();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delivery confirmation failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (isAlreadyDelivered) {
    const sigDelivery = (project.completion_signoffs || {}).delivery || {};
    const when = sigDelivery.completed_at
      ? new Date(sigDelivery.completed_at).toLocaleDateString("en-IN",
          { day: "2-digit", month: "short", year: "numeric" })
      : "—";
    return (
      <div
        data-testid="delivery-progress-strip"
        style={{
          background: "#FFFFFF",
          border: "1px solid #E9E2D6",
          borderRadius: 12,
          padding: 24,
          marginBottom: 28,
          boxShadow: "0 2px 6px rgba(60,46,20,.05)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: "#B08D55",
              marginBottom: 6,
            }}
          >
            DELIVERY PROGRESS
          </div>
          <div style={{ fontSize: 14, color: "#22382C" }} data-testid="delivery-progress-confirmed">
            <span style={{ color: "#3F6B51", fontWeight: 700 }}>✓</span> Delivery confirmed on <strong>{when}</strong>
            {sigDelivery.completed_by && <span style={{ color: "#76705F" }}> by {sigDelivery.completed_by}</span>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="delivery-progress-strip"
      style={{
        background: "#FFFFFF",
        border: "1px solid #E9E2D6",
        borderRadius: 12,
        padding: 24,
        marginBottom: 28,
        boxShadow: "0 2px 6px rgba(60,46,20,.05)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 16,
        flexWrap: "wrap",
      }}
    >
      <div>
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: "#B08D55",
            marginBottom: 6,
          }}
        >
          DELIVERY PROGRESS
        </div>
        <div style={{ fontSize: 14, color: "#22382C" }} data-testid="delivery-progress-count">
          {delivered} of {total} deliverable{total === 1 ? "" : "s"} complete
        </div>
      </div>
      <span title={allDone ? "" : `Awaiting ${total - delivered} deliverable${total - delivered === 1 ? "" : "s"}`}
            data-testid="confirm-delivery-wrap">
        <button
          onClick={() => setConfirmOpen(true)}
          disabled={!allDone}
          data-testid="confirm-delivery-button"
          style={{
            padding: "11px 20px",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            borderRadius: 8,
            background: allDone ? "#22382C" : "#EFE8DC",
            color: allDone ? "#F4EFE4" : "#B7AE9E",
            cursor: allDone ? "pointer" : "not-allowed",
            transition: "background-color .15s, color .15s",
          }}
        >
          ✓ Confirm delivery complete
        </button>
      </span>
      {confirmOpen && (
        <ConfirmDeliveryModal
          projectName={project.partner_name || project.id}
          onClose={() => setConfirmOpen(false)}
          onConfirm={confirm}
          submitting={submitting}
        />
      )}
    </div>
  );
}

function ConfirmDeliveryModal({ projectName, onClose, onConfirm, submitting }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         style={{ background: "rgba(14,48,48,0.5)" }}
         data-testid="confirm-delivery-modal">
      <div className="bg-white p-6 w-[min(440px,92vw)]" style={{ borderRadius: 0 }}
           onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display text-xl mb-3" style={{ color: "var(--brand-ink)" }}>
          Confirm delivery complete?
        </h3>
        <p className="text-sm text-brand-muted mb-5">
          This marks <span className="text-brand-ink font-medium">{projectName}</span> as <em>Delivered</em>.
          All editing work is signed off as complete. The Accounts team will take over from here.
        </p>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} disabled={submitting}
                  className="px-3 py-2 text-xs tracking-[0.18em] uppercase text-brand-muted"
                  data-testid="confirm-delivery-cancel">Cancel</button>
          <button onClick={onConfirm} disabled={submitting}
                  className="px-3 py-2 text-xs tracking-[0.18em] uppercase text-white disabled:opacity-50"
                  style={{ background: "var(--brand-champagne)" }}
                  data-testid="confirm-delivery-confirm">
            {submitting ? "Confirming…" : "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
}

